/**
 * Slower-planet-first ordering for aspect pairs.
 *
 * An aspect between two bodies is conventionally read with the slower one as the anchor: Saturn
 * square Mars, not Mars square Saturn. The faster body is what moves through the aspect, so it is
 * the one whose position is measured against the other. Two studies of the same pair entered in
 * opposite orders should therefore produce the same study, and before this they did not — the
 * label, and anything keyed off it, differed by which dropdown the user happened to fill first.
 *
 * Rank is by mean geocentric motion, slowest first. The numbers are not used, only the order, so
 * this is a deliberate ranking rather than a computation:
 *
 *   Pluto < Neptune < Uranus < Saturn < Rahu < Jupiter < Mars < Sun < Venus < Mercury < Moon
 *
 * Two placements worth stating, because neither is self-evident:
 *
 * Rahu (the lunar north node) sits between Saturn and Jupiter. Its mean nodal period is about
 * 18.6 years — slower than Jupiter's 11.9 and faster than Saturn's 29.5. It is not in the
 * slowest-to-fastest list people usually recite, so it would otherwise land wherever the data
 * structure happened to put it.
 *
 * Sun, Mercury and Venus all average roughly a year geocentrically, so mean motion alone does not
 * separate them. They are ordered Sun < Venus < Mercury, which is the traditional ranking by
 * apparent speed and matches how the pairs are conventionally named.
 */

/** Slowest first. Index is the rank; the values are ordering only, not speeds. */
export const PLANETS_SLOWEST_FIRST = [
  'Pluto',
  'Neptune',
  'Uranus',
  'Saturn',
  'Rahu',
  'Jupiter',
  'Mars',
  'Sun',
  'Venus',
  'Mercury',
  'Moon',
];

/** Names that mean the same body as one already ranked. */
const ALIASES = {
  'north node': 'Rahu',
  'true node': 'Rahu',
  'south node': 'Ketu',
  // Ketu is always opposite Rahu, so it moves at the same rate and ranks with it.
  ketu: 'Rahu',
};

const RANK = PLANETS_SLOWEST_FIRST.reduce((acc, name, i) => {
  acc[name.toLowerCase()] = i;
  return acc;
}, {});

/**
 * Rank of a body, lower being slower.
 *
 * Unknown names rank last (fastest). That keeps them in the Planet 2 slot rather than promoting
 * an unrecognised body to the anchor position, which is the more conservative guess.
 */
export function speedRank(planet) {
  if (!planet) return Number.MAX_SAFE_INTEGER;
  const key = String(planet).trim().toLowerCase();
  const resolved = ALIASES[key] ? ALIASES[key].toLowerCase() : key;
  const rank = RANK[resolved];
  return rank === undefined ? Number.MAX_SAFE_INTEGER : rank;
}

/** True when `a` moves more slowly than `b`. */
export function isSlower(a, b) {
  return speedRank(a) < speedRank(b);
}

/**
 * Orders a pair so the slower body is first.
 *
 * Returns the pair unchanged when it is already ordered, when either name is missing, or when both
 * rank equally — swapping equals would make the result depend on argument order, which is exactly
 * the instability this exists to remove.
 *
 * @returns {{planet1:string, planet2:string, swapped:boolean}}
 */
export function orderBySpeed(planet1, planet2) {
  const unchanged = { planet1, planet2, swapped: false };
  if (!planet1 || !planet2) return unchanged;
  if (speedRank(planet2) < speedRank(planet1)) {
    return { planet1: planet2, planet2: planet1, swapped: true };
  }
  return unchanged;
}

export default orderBySpeed;
